import ReactDatePicker from 'react-datepicker';
import { useMemo, useState } from 'react';

import { ClearDate, DataRangeWrapper, DateInput, DataRangePopup } from './styles';
import { countDatesOccurrences } from './utilities';
import { DatePickerInputProps, HighlightDatesType } from './types';

const DatePickerInput = (props: DatePickerInputProps) => {
  const {
    selected,
    onChange,
    startDate,
    endDate,
    selectsRange,
    label,
    disabled,
    maxDate,
    minDate,
    showDisabledMonthNavigation,
    inline,
    dotDays,
    readOnlyCalendar,
    dotRangeDays,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(readOnlyCalendar ? true : false);

  const countedDotRangeDays = dotRangeDays?.length ? countDatesOccurrences(dotRangeDays) : {};

  // Prepare dots data for calendar if exist.
  const highlightDates = useMemo(() => {
    //Mapping single day dots.
    if (dotDays?.length) {
      return [{ dottedDays: dotDays }, { 'dottedDays::after': dotDays }];
    }
    //Mapping multiple days on dots.
    if (countedDotRangeDays && Object.keys(countedDotRangeDays)?.length) {
      const keys = Object.keys(countedDotRangeDays);
      //Setup different classes. Each class has their own opacity - style. Based on the count, it will have different opacity - style.
      const dottedDays: HighlightDatesType = { dottedDays: [] };
      const dottedDaysAfter: HighlightDatesType = { 'dottedDays::after': [] };
      const dottedDays30: HighlightDatesType = { 'dottedDays-30': [] };
      const dottedDays30After: HighlightDatesType = { 'dottedDays-30::after': [] };
      const dottedDays60: HighlightDatesType = { 'dottedDays-60': [] };
      const dottedDays60After: HighlightDatesType = { 'dottedDays-60::after': [] };
      keys.forEach((key) => {
        const count = countedDotRangeDays[key];
        const date = new Date(key);
        //Choosing class based on count.
        if (count <= 2) {
          dottedDays30['dottedDays-30'].push(date);
          dottedDays30After['dottedDays-30::after'].push(date);
        } else if (count <= 4) {
          dottedDays60['dottedDays-60'].push(date);
          dottedDays60After['dottedDays-60::after'].push(date);
        } else {
          dottedDays.dottedDays.push(date);
          dottedDaysAfter['dottedDays::after'].push(date);
        }
      });
      return [
        dottedDays,
        dottedDaysAfter,
        dottedDays30,
        dottedDays30After,
        dottedDays60,
        dottedDays60After,
      ];
    }
    return [];
  }, [dotDays, countedDotRangeDays]);

  return (
    <DataRangeWrapper>
      {!readOnlyCalendar && (
        <DateInput
          className={`${disabled ? 'disabled' : ''} `}
          onClick={() => {
            if (disabled) return;
            setIsOpen((prevState) => !prevState);
          }}
        >
          {startDate && !endDate
            ? startDate.toDateString()
            : selected && !endDate
            ? selected.toDateString()
            : startDate && endDate
            ? `${startDate.toDateString()} - ${endDate.toDateString()}`
            : label}

          {(startDate || endDate) && (
            <ClearDate
              onClick={(evt) => {
                evt.stopPropagation();
                onChange(selectsRange ? [null, null] : null);
              }}
            >
              x
            </ClearDate>
          )}
        </DateInput>
      )}
      {isOpen && (
        <DataRangePopup position={readOnlyCalendar ? 'static' : 'absolute'}>
          <ReactDatePicker
            selected={selected}
            onChange={(date: any) => {
              if (readOnlyCalendar) return;
              if ((selectsRange && date?.[0] && date?.[1]) || (!selectsRange && date))
                setIsOpen(false);
              onChange(date);
            }}
            startDate={startDate ?? undefined}
            endDate={endDate}
            selectsRange={selectsRange as any}
            showDisabledMonthNavigation={showDisabledMonthNavigation}
            maxDate={maxDate}
            minDate={minDate}
            inline={inline}
            highlightDates={highlightDates ?? []}
          />
        </DataRangePopup>
      )}
    </DataRangeWrapper>
  );
};

export default DatePickerInput;
